// The widget's half of the Flix Identity bridge.
//
// A customer signed in to Flix Identity has no Shopify customer session, so
// the App Proxy cannot tell the backend who they are. Instead:
//
//   1. this asks Flix Identity — with the customer's own HttpOnly session
//      cookie, which this script never sees — for a short-lived signed
//      handoff naming the account and its linked Shopify customer;
//   2. it passes that handoff to the support backend, which verifies the
//      signature and hands back a 10-minute customer session.
//
// Nothing durable is ever held here. The handoff is spent the moment it is
// obtained; the customer session lives in memory for this page only and is
// re-established from the Flix Identity cookie on the next one. No token is
// written to localStorage, sessionStorage or a cookie.

export type BridgeOutcome =
  // The backend now knows this visitor as `customerId`.
  | { status: 'authenticated'; customerId: string; token: string; expiresAtMs: number }
  // Nobody is signed in to Flix Identity in this browser.
  | { status: 'signed-out' }
  // Signed in, but the account has no linked Shopify customer to speak for.
  | { status: 'link-missing' }
  // The bridge could not be used (switched off, unreachable, refused). The
  // widget then behaves exactly as it did before the bridge existed.
  | { status: 'unavailable' };

export interface FlixIdentityBridgeOptions {
  // The Flix Identity origin for the storefront domain being browsed.
  identityBaseUrl: string;
  // The support API base the widget already uses (the App Proxy path).
  apiBaseUrl: string;
  fetchImpl?: typeof fetch;
}

// What the support backend says about the bridge (GET /chat/auth/config).
export interface BridgeAvailability {
  // The bridge is on. False is OFF, and off means the widget makes no
  // request to Flix Identity of any kind, creates no verifier, and behaves
  // exactly as it did before the bridge existed.
  enabled: boolean;
  // Flix Identity may be offered to a signed-out visitor as the way to sign
  // in. False during a restricted rollout: the public keeps the existing
  // sign-in, and only a visitor already signed in to Flix Identity is tried.
  signInOffered: boolean;
}

const BRIDGE_OFF: BridgeAvailability = { enabled: false, signInOffered: false };

export interface FlixIdentityBridge {
  // Asks the backend first (see availability) and stops there when the
  // bridge is off: nothing is sent to Flix Identity and nothing is created.
  establish(): Promise<BridgeOutcome>;
  // Whether the backend has the bridge on, and for whom. Asked of the
  // support backend only, once per page, and remembered. Anything short of
  // a clear yes — a refusal, a network failure, an odd body — is off.
  availability(): Promise<BridgeAvailability>;
  revoke(token: string): Promise<void>;
  signInUrl(returnTo: string, locale: string): string;
}

// What the host page already knows about its own Flix Identity session:
// the storefront's account client's answer, or 'none' when the page offers
// no such answer at all.
export type HostAuthState = 'signed-in' | 'signed-out' | 'none';

// Whether the bridge has any part to play in this page view, and the bridge
// itself when it does. It has none for a visitor the page already has a
// Shopify customer for — that session is used as it always was — and none
// on a page with no Flix Identity origin it could use. Null means exactly
// that: no bridge exists, so nothing can be asked of it.
export function bridgeFor(
  shopifyCustomerId: string | undefined,
  identityBaseUrl: string | null,
  apiBaseUrl: string,
  fetchImpl?: typeof fetch,
): FlixIdentityBridge | null {
  if (shopifyCustomerId || identityBaseUrl === null) {
    return null;
  }
  return createFlixIdentityBridge({ identityBaseUrl, apiBaseUrl, fetchImpl });
}

// The one place a page view decides whether to go to the network.
//
// A host that says "signed out" is believed, and no request is made at all.
// For anyone else the bridge asks the support backend first (see
// establish); when that says the bridge is off, the outcome is
// 'unavailable' and Flix Identity never hears from this page.
export function establishForHost(
  bridge: FlixIdentityBridge,
  hostState: HostAuthState,
): Promise<BridgeOutcome> {
  return hostState === 'signed-out'
    ? Promise.resolve({ status: 'signed-out' })
    : bridge.establish();
}

// A note-to-self across the round trip to Flix Identity: "the visitor left
// from the open widget to sign in". It is what lets the widget reopen when
// they come back, instead of making them find the launcher again. It is a
// timestamp and nothing else — not a credential, and worth nothing to
// anyone who reads it. sessionStorage keeps it to this tab; the time limit
// keeps a sign-in that was abandoned from reopening the widget hours later.
const SIGN_IN_STARTED_KEY = 'flix-chat-widget-sign-in-started';
const SIGN_IN_ROUND_TRIP_MS = 10 * 60 * 1000;

export function markSignInStarted(): void {
  try {
    sessionStorage.setItem(SIGN_IN_STARTED_KEY, String(Date.now()));
  } catch {
    // Storage unavailable: the widget simply stays closed on return.
  }
}

// True once, for a sign-in begun from this tab within the last few minutes.
export function consumeSignInStarted(): boolean {
  try {
    const raw = sessionStorage.getItem(SIGN_IN_STARTED_KEY);
    if (raw === null) {
      return false;
    }
    sessionStorage.removeItem(SIGN_IN_STARTED_KEY);
    const startedAt = Number(raw);
    return Number.isFinite(startedAt) && Date.now() - startedAt < SIGN_IN_ROUND_TRIP_MS;
  } catch {
    return false;
  }
}

// Whether two hosts are the same *site* — which is what decides whether the
// browser will send Flix Identity's SameSite cookie on a request made from
// this page. The bridge is only safe to use when it will: from another site
// the handoff request arrives with no session, and a visitor sent to sign in
// would come back signed in and still unrecognised, in a loop.
//
// Compared by registrable domain, taken as the last two labels. That is
// exact for the domains this runs on (.com, .net, .co) and deliberately
// simple; a multi-label public suffix (example.co.uk) would need the public
// suffix list, and is not a case that exists here.
export function isSameSite(pageHostname: string, identityHostname: string): boolean {
  const page = pageHostname.toLowerCase();
  const identity = identityHostname.toLowerCase();
  if (page === identity) {
    return true;
  }

  const registrable = (hostname: string): string | null => {
    const labels = hostname.split('.').filter((label) => label.length > 0);
    // An IP address or a single-label host has no registrable domain.
    if (labels.length < 2 || /^[0-9]+$/.test(labels[labels.length - 1]!)) {
      return null;
    }
    return labels.slice(-2).join('.');
  };

  const pageDomain = registrable(page);
  return pageDomain !== null && pageDomain === registrable(identity);
}

// Where a signed-in visitor goes to complete the link between their Flix
// account and their store customer profile: the storefront's own account
// screen. Taken from configuration when given, otherwise from the page's
// Shopify routes — and in either case only ever a URL on this page's own
// origin, so the action can never be turned into a link somewhere else.
export function resolveAccountUrl(
  configured: string | undefined,
  shopifyRoot: unknown,
  pageOrigin: string,
): string | null {
  const sameOrigin = (candidate: string): string | null => {
    try {
      const url = new URL(candidate, pageOrigin);
      return url.origin === pageOrigin ? url.toString() : null;
    } catch {
      return null;
    }
  };

  if (typeof configured === 'string' && configured.trim().length > 0) {
    return sameOrigin(configured.trim());
  }

  // Shopify.routes.root is "/" or "/en/": the same page, in the language
  // being browsed.
  if (typeof shopifyRoot === 'string' && shopifyRoot.startsWith('/')) {
    const root = shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;
    return sameOrigin(`${root}pages/account`);
  }

  return null;
}

function trimTrailingSlashes(url: string): string {
  return url.replace(/\/+$/, '');
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// The verifier stays in this function's caller; only its hash goes to Flix
// Identity, which signs it into the handoff. The backend then requires the
// verifier itself — so a handoff seen anywhere along the way is useless to
// anyone but the page that asked for it.
async function createVerifierPair(): Promise<{ verifier: string; challenge: string } | null> {
  if (typeof crypto === 'undefined' || !crypto.subtle || !crypto.getRandomValues) {
    return null;
  }

  const verifier = toBase64Url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));

  return { verifier, challenge: toBase64Url(new Uint8Array(digest)) };
}

function readString(value: unknown, field: string): string | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const found = (value as Record<string, unknown>)[field];
  return typeof found === 'string' && found.length > 0 ? found : null;
}

export function createFlixIdentityBridge(options: FlixIdentityBridgeOptions): FlixIdentityBridge {
  const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const identityBaseUrl = trimTrailingSlashes(options.identityBaseUrl);
  const apiBaseUrl = trimTrailingSlashes(options.apiBaseUrl);

  async function requestHandoff(
    challenge: string,
  ): Promise<{ assertion: string } | Exclude<BridgeOutcome['status'], 'authenticated'>> {
    // `credentials: 'include'` is what carries the Flix Identity session:
    // an HttpOnly cookie on a host inside this storefront's own site, sent
    // by the browser and never readable from here.
    const csrfResponse = await fetchImpl(`${identityBaseUrl}/auth/csrf`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const csrfToken = csrfResponse.ok ? readString(await csrfResponse.json(), 'csrfToken') : null;
    if (csrfToken === null) {
      return 'unavailable';
    }

    const response = await fetchImpl(`${identityBaseUrl}/support/handoff`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ challenge }),
    });

    if (response.status === 401) {
      return 'signed-out';
    }
    if (response.status === 409) {
      return 'link-missing';
    }
    if (!response.ok) {
      return 'unavailable';
    }

    const assertion = readString(await response.json(), 'assertion');
    return assertion === null ? 'unavailable' : { assertion };
  }

  async function readAvailability(): Promise<BridgeAvailability> {
    try {
      const response = await fetchImpl(`${apiBaseUrl}/chat/auth/config`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        return BRIDGE_OFF;
      }
      const body: unknown = await response.json();
      if (typeof body !== 'object' || body === null) {
        return BRIDGE_OFF;
      }
      const config = body as Record<string, unknown>;
      const enabled = config.flixIdentity === true;
      return { enabled, signInOffered: enabled && config.flixIdentitySignIn === true };
    } catch {
      return BRIDGE_OFF;
    }
  }

  let availability: Promise<BridgeAvailability> | null = null;

  return {
    availability(): Promise<BridgeAvailability> {
      availability ??= readAvailability();
      return availability;
    },

    async establish(): Promise<BridgeOutcome> {
      try {
        // The backend is asked before anything else happens. Off, this is
        // where it ends — no verifier is made and Flix Identity never
        // hears from this page.
        if (!(await this.availability()).enabled) {
          return { status: 'unavailable' };
        }

        const pair = await createVerifierPair();
        if (pair === null) {
          return { status: 'unavailable' };
        }

        const handoff = await requestHandoff(pair.challenge);
        if (typeof handoff === 'string') {
          return { status: handoff };
        }

        const response = await fetchImpl(`${apiBaseUrl}/chat/auth/flix`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ assertion: handoff.assertion, verifier: pair.verifier }),
        });
        if (!response.ok) {
          return { status: 'unavailable' };
        }

        const body: unknown = await response.json();
        const token = readString(body, 'token');
        const customerId = readString(body, 'customerId');
        const expiresAt = readString(body, 'expiresAt');
        const expiresAtMs = expiresAt === null ? Number.NaN : Date.parse(expiresAt);
        if (token === null || customerId === null || Number.isNaN(expiresAtMs)) {
          return { status: 'unavailable' };
        }

        return { status: 'authenticated', customerId, token, expiresAtMs };
      } catch {
        // A network failure, a CORS refusal, a malformed body: none of them
        // may break the widget. It falls back to what it did before.
        return { status: 'unavailable' };
      }
    },

    async revoke(token: string): Promise<void> {
      try {
        await fetchImpl(`${apiBaseUrl}/chat/auth/session`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
          keepalive: true,
        });
      } catch {
        // Best effort: the session expires on its own within minutes.
      }
    },

    // Flix Identity's own sign-in entry point — the same one the storefront's
    // account screen uses. It validates return_to against its allowlist of
    // first-party origins and ignores anything else, so this cannot be
    // turned into a redirect to somewhere else.
    signInUrl(returnTo: string, locale: string): string {
      const url = new URL(`${identityBaseUrl}/auth/start`);
      url.searchParams.set('ui_locales', locale);
      url.searchParams.set('return_to', returnTo);
      return url.toString();
    },
  };
}
